import React, { useState, useEffect } from 'react';
import axios from 'axios';

function AdminPage() {
  const [trials, setTrials] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchTrials();
  }, []);

  const fetchTrials = async () => {
    try {
      const res = await axios.get('/api/admin/trials');
      setTrials(res.data.trials);
      setStats(res.data.stats);
      setLoading(false);
    } catch (err) {
      setError('Fehler beim Laden: ' + (err.response?.data?.error || err.message));
      setLoading(false);
    }
  };

  const handleAction = async (trialId, action) => {
    try {
      if (action === 'delete') {
        if (!window.confirm('Trial wirklich löschen? Container wird entfernt!')) return;
        await axios.delete('/api/admin/trials/' + trialId);
      } else {
        await axios.post('/api/admin/trials/' + trialId + '/' + action);
      }
      fetchTrials();
    } catch (err) {
      alert('Fehler: ' + (err.response?.data?.error || err.message));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const getStatusClass = (status) => {
    if (status === 'active') return 'bg-green-600';
    if (status === 'pending') return 'bg-yellow-600';
    return 'bg-red-600';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        Laden...
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 text-red-400 flex items-center justify-center">
        {error}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4 md:p-8">
      <h1 className="text-2xl md:text-3xl font-bold mb-6">Trial Admin Panel</h1>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-gray-800 p-3 md:p-4 rounded">
          <div className="text-xl md:text-2xl font-bold">{stats.total || 0}</div>
          <div className="text-gray-400 text-sm">Gesamt</div>
        </div>
        <div className="bg-green-900 p-3 md:p-4 rounded">
          <div className="text-xl md:text-2xl font-bold">{stats.active || 0}</div>
          <div className="text-gray-400 text-sm">Aktiv</div>
        </div>
        <div className="bg-yellow-900 p-3 md:p-4 rounded">
          <div className="text-xl md:text-2xl font-bold">{stats.pending || 0}</div>
          <div className="text-gray-400 text-sm">Ausstehend</div>
        </div>
        <div className="bg-red-900 p-3 md:p-4 rounded">
          <div className="text-xl md:text-2xl font-bold">{stats.stopped || 0}</div>
          <div className="text-gray-400 text-sm">Gestoppt</div>
        </div>
      </div>

      <div className="space-y-4">
        {trials.map(trial => (
          <div key={trial._id} className="bg-gray-800 rounded p-4">
            <div className="flex flex-wrap justify-between items-start gap-2 mb-3">
              <div>
                <h3 className="font-bold text-lg">{trial.companyName}</h3>
                <p className="text-gray-400 text-sm">{trial.email}</p>
                {trial.phone && <p className="text-gray-500 text-sm">{trial.phone}</p>}
                {trial.siaIdent && (
                  <p className="text-yellow-400 text-sm font-mono">SIA Ident: {trial.siaIdent}</p>
                )}
              </div>
              <span className={"px-2 py-1 rounded text-sm " + getStatusClass(trial.status)}>
                {trial.status}
              </span>
            </div>
            
            {trial.subdomain && (
              <div className="bg-gray-700 rounded p-3 mb-3">
                <div className="text-xs text-gray-400 mb-1">Trial-Link:</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <a 
                    href={"https://" + trial.subdomain + ".trial.persec-technik.de"} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:underline break-all text-sm"
                  >
                    https://{trial.subdomain}.trial.persec-technik.de
                  </a>
                  <button 
                    onClick={() => copyToClipboard("https://" + trial.subdomain + ".trial.persec-technik.de")}
                    className="text-gray-400 hover:text-white text-xs bg-gray-600 px-2 py-1 rounded"
                  >
                    Kopieren
                  </button>
                </div>
              </div>
            )}
            
            <div className="flex flex-wrap gap-2 text-sm text-gray-400">
              <span>Erstellt: {new Date(trial.createdAt).toLocaleDateString('de-DE')}</span>
              {trial.expiresAt && (
                <span>| Laeuft ab: {new Date(trial.expiresAt).toLocaleDateString('de-DE')}</span>
              )}
              {trial.containerPort && (
                <span>| Port: {trial.containerPort}</span>
              )}
            </div>
            
            <div className="mt-3 flex gap-2">
              {trial.status === 'active' && (
                <button
                  onClick={() => handleAction(trial.trialId, 'stop')}
                  className="bg-red-600 px-3 py-1 rounded text-sm hover:bg-red-700"
                >
                  Stoppen
                </button>
              )}
              {trial.status === 'stopped' && (
                <button
                  onClick={() => handleAction(trial.trialId, 'restart')}
                  className="bg-green-600 px-3 py-1 rounded text-sm hover:bg-green-700"
                >
                  Starten
                </button>
              )}
              {(trial.status === 'active' || trial.status === 'stopped') && (
                <button
                  onClick={() => handleAction(trial.trialId, 'delete')}
                  className="bg-gray-600 px-3 py-1 rounded text-sm hover:bg-gray-500"
                >
                  Löschen
                </button>
              )}
            </div>
          </div>
        ))}
        
        {trials.length === 0 && (
          <div className="text-center text-gray-500 py-8">Keine Trials vorhanden</div>
        )}
      </div>
    </div>
  );
}

export default AdminPage;
